import React, { useState } from "react";
import { X, Globe, Trash2, AlertTriangle, Info, Pencil } from "lucide-react";
import PreviewSection from "./PreviewSection";

const DeleteConfirmation = ({ onConfirm, onCancel, isDarkMode }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
    <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-md w-full`}>
      <div className="flex items-center space-x-3 text-red-500 mb-4">
        <AlertTriangle size={24} />
        <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Content verwijderen
        </h3>
      </div>
      <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} mb-6`}>
        Weet je zeker dat je deze content wilt verwijderen? Dit kan niet ongedaan worden gemaakt.
      </p>
      <div className="flex space-x-3 justify-end">
        <button
          onClick={onCancel}
          className={`px-4 py-2 rounded-md ${
            isDarkMode 
              ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Annuleren
        </button>
        <button
          onClick={onConfirm}
          className="px-4 py-2 rounded-md bg-red-500 text-white hover:bg-red-600"
        >
          Verwijderen
        </button>
      </div>
    </div>
  </div>
);

const ContentModal = ({ content, isOpen, onClose, onDelete, onEdit, isDarkMode }) => {
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  if (!isOpen || !content) return null;

  const metadata = {
    title: content.title,
    description: content.description,
    images: content.images || (content.image ? [{ url: content.image }] : []),
    url: content.url,
    domain: content.url ? new URL(content.url).hostname : undefined,
    mainImageIndex: content.mainImageIndex || 0,
    isYoutube: content.metadata?.isYoutube,
    videoId: content.metadata?.videoId,
    channelTitle: content.metadata?.channelTitle,
    ...content.metadata
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
      <div className="min-h-screen px-4 py-6 flex items-center justify-center">
        <div className={`${isDarkMode ? 'bg-gray-900' : 'bg-white'} rounded-lg p-6 w-full max-w-4xl relative`}>
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {content.title}
            </h2>
            <button 
              onClick={onClose} 
              className={isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}
            >
              <X size={24} />
            </button>
          </div>

          {/* Content */}
          <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
            <div className="space-y-4">
              <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                {content.description}
              </p>
              
              {/* Metadata */}
              <div className={`grid grid-cols-2 gap-4 py-3 mt-4 border-y ${
                isDarkMode ? 'border-gray-700' : 'border-gray-200'
              }`}>
                <div>
                  <span className={`text-xs uppercase ${
                    isDarkMode ? 'text-gray-500' : 'text-gray-400'
                  }`}>Toegevoegd door</span>
                  <p className={`text-sm mt-1 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>{content.contributor}</p>
                </div>
                <div>
                  <span className={`text-xs uppercase ${
                    isDarkMode ? 'text-gray-500' : 'text-gray-400'
                  }`}>Type</span>
                  <p className={`text-sm mt-1 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>{content.contentType}</p>
                </div>
              </div>
                        {/* Action buttons */}
          <div className="flex flex-col gap-3 pt-4">
          
            {content.url && (
              <a
                href={content.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`w-full py-2.5 rounded-lg flex items-center justify-center gap-2 ${
                  isDarkMode 
                    ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Globe size={16} />
                <span>Bron bekijken</span>
              </a>
            )}
              <button
              onClick={onClose}
              className="w-full py-2.5 rounded-lg flex items-center justify-center gap-2 bg-[#e8c2d9] text-gray-900 hover:bg-opacity-90"
            >
              <Info size={16} />
              <span>Sluiten</span>
            </button>
          </div>
{/* Preview */}
              <PreviewSection 
                metadata={metadata} 
                isLoading={false} 
                isDarkMode={isDarkMode}
                expandedHeight={true} 
              />
            </div>
          </div>

          {/* Delete and Edit buttons */}
          <div className={`mt-6 pt-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowDeleteConfirmation(true)}
                className="text-red-500 hover:text-red-600 flex items-center"
              >
                <Trash2 size={16} className="mr-1" />
                Verwijderen
              </button>
              <button
                onClick={() => {
                  onEdit?.(content);
                  onClose();
                }}
                className="text-blue-500 hover:text-blue-600 flex items-center"
              >
                <Pencil size={16} className="mr-1" />
                Bewerken
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete confirmation dialog */}
      {showDeleteConfirmation && (
        <DeleteConfirmation
          onConfirm={() => {
            if (!content.id) {
              console.error('No content ID found for deletion');
              alert('Er ging iets mis bij het verwijderen. Probeer het opnieuw.');
              return;
            }
            onDelete(content.id);
            setShowDeleteConfirmation(false);
          }}
          onCancel={() => setShowDeleteConfirmation(false)}
          isDarkMode={isDarkMode}
        />
      )}
    </div>
  );
};

export default ContentModal;